/* ============================================================================
   SNAPGOODY — SHOPIFY BRIDGE
   ----------------------------------------------------------------------------
   The front end (core.js / editor.js / shop.js / site.js) is shared byte for
   byte with the static build. This file is the only Shopify-specific layer: it
   rebuilds SG.catalog, SG.media and SG.cart from Liquid-emitted JSON instead of
   the local content/ files.

   WHAT LIQUID EMITS
   -----------------
     <script type="application/json" id="sg-data">{
        market, currency,
        collections: [{handle,title,blurb,image}],
        products:    [{...see normalise() below}],
        fittingRoom: [{key,title,example,exampleLabel,image}],
        slots:       { "hero.slide-1": {src,label,size,focal}, ... }
     }</script>

   Everything a product needs for the studio lives in metafields — see
   PRINTIFY.md for the full metafield list and the order handoff.
   ========================================================================== */
(function (global) {
  'use strict';
  var SG = global.SG;
  if (!SG) { console.error('[snapgoody] core.js must load before theme-bridge.js'); return; }

  var shop = global.SG_SHOP || {};
  var raw = {};
  var node = document.getElementById('sg-data');
  if (node) { try { raw = JSON.parse(node.textContent); } catch (e) { console.error('[snapgoody] bad sg-data', e); } }

  /* ================================================================ REGION ==*/
  var MARKET_TO_REGION = { uk: 'UK', gb: 'UK', eu: 'EU', europe: 'EU', us: 'US', 'north-america': 'US' };
  SG.region = MARKET_TO_REGION[String(shop.market || '').toLowerCase()] || 'UK';

  /* Shopify already prices in the customer's currency, so no conversion here.
     Prices arrive as integer minor units in the active presentment currency. */
  Object.keys(SG.regions).forEach(function (k) { SG.regions[k].rate = 1; });
  if (shop.currency) {
    SG.regions[SG.region].currency = shop.currency;
    SG.regions[SG.region].locale = document.documentElement.lang || 'en-GB';
  }

  /* ================================================================= MEDIA ==*/
  global.SG_MEDIA = {
    basePath: '',
    slots: raw.slots || {},
    products: {},
    mockups: {}
  };
  (raw.products || []).forEach(function (p) {
    global.SG_MEDIA.products[p.handle] = p.images || [];
    if (p.mockup) global.SG_MEDIA.mockups[p.handle] = p.mockup;
  });

  /* =============================================================== CONTENT ==*/
  global.SG_CONTENT = global.SG_CONTENT || {};

  /* =============================================================== CATALOG ==*/
  function normalise(p) {
    return {
      handle: p.handle,
      title: p.title,
      vendor: p.vendor,
      price: p.price,                       // integer minor units
      compareAt: p.compareAt || null,
      collections: p.collections || [],
      badge: p.badge || '',
      family: p.family || '',
      familyLabel: p.familyLabel || '',
      luxury: !!p.luxury,
      blurb: p.blurb || '',
      description: p.description || '',
      details: p.details || [],
      options: p.options || [],
      surfaces: p.surfaces && p.surfaces.length ? p.surfaces : [defaultSurface()],
      /* Market-scoped overrides — see PRINTIFY.md. Absent for most products. */
      optionsByMarket: p.optionsByMarket || null,
      surfacesByMarket: p.surfacesByMarket || null,
      detailsByMarket: p.detailsByMarket || null,
      optionNotes: p.optionNotes || null,
      allowBackground: p.allowBackground !== false,
      mock: p.mock || { ratio: 1, radius: '4px' },
      bundle: p.bundle || null,
      regions: p.regions || {},
      personalisable: p.personalisable !== false,
      variants: p.variants || [],           // [{id, options:[..], available}]
      defaultVariantId: p.defaultVariantId || null,
      url: p.url || ('/products/' + p.handle)
    };
  }
  function defaultSurface() {
    return {
      id: 'front', label: 'Print area', widthMM: 200, heightMM: 200, dpi: 300,
      area: { x: 0.1, y: 0.1, w: 0.8, h: 0.8 }, shape: 'rect', radius: 0, safe: 0.06, bg: '#FFFFFF'
    };
  }

  var PRODUCTS = (raw.products || []).map(normalise);
  var byHandle = {};
  PRODUCTS.forEach(function (p) { byHandle[p.handle] = p; });

  var COLLECTIONS = (raw.collections || []).map(function (c) {
    return { handle: c.handle, title: c.title, blurb: c.blurb || '', tagline: c.tagline || '', slot: 'collection.' + c.handle };
  });
  COLLECTIONS.forEach(function (c) {
    if (c.image) global.SG_MEDIA.slots['collection.' + c.handle] = { src: c.image, label: c.title };
  });

  function pick(map, fallback, region) {
    if (!map) return fallback;
    return map[region] || map[SG.region] || fallback;
  }

  SG.catalog = {
    products: PRODUCTS,
    collections: COLLECTIONS,
    fittingRoom: raw.fittingRoom || [],
    months: ['January', 'February', 'March', 'April', 'May', 'June',
             'July', 'August', 'September', 'October', 'November', 'December'],

    get: function (h) { return byHandle[h] || null; },

    /* ── market-aware accessors. Always read a product through these. ───── */
    optionsFor: function (product, region) {
      if (!product) return [];
      return pick(product.optionsByMarket, product.options || [], region || SG.region);
    },
    surfacesFor: function (product, region) {
      if (!product) return [];
      return pick(product.surfacesByMarket, product.surfaces || [], region || SG.region);
    },
    detailsFor: function (product, region) {
      if (!product) return [];
      return pick(product.detailsByMarket, product.details || [], region || SG.region);
    },
    surfaceBg: function (surf, options) {
      if (!surf || !surf.bgFrom) return null;
      var v = (options || {})[surf.bgFrom.option];
      return (surf.bgFrom.map || {})[v] || null;
    },
    optionKey: function (product, options, region, names) {
      var list = SG.catalog.optionsFor(product, region);
      if (names) list = list.filter(function (o) { return names.indexOf(o.name) !== -1; });
      return list.map(function (o) { return (options || {})[o.name] || o.values[0]; }).join('|');
    },
    inCollection: function (h) {
      return PRODUCTS.filter(function (p) { return (p.collections || []).indexOf(h) !== -1; });
    },
    collection: function (h) {
      return COLLECTIONS.filter(function (c) { return c.handle === h; })[0] || null;
    },
    family: function (n) { return PRODUCTS.filter(function (p) { return p.family === n; }); },
    search: function (q) {
      q = String(q || '').trim().toLowerCase();
      if (!q) return [];
      return PRODUCTS.filter(function (p) {
        return (p.title + ' ' + p.blurb + ' ' + p.vendor + ' ' + p.handle).toLowerCase().indexOf(q) !== -1;
      }).slice(0, 8);
    },

    /* Which Shopify product fulfils this handle in the active market? ------ */
    forRegion: function (product, region) {
      if (!product) return null;
      var r = (product.regions || {})[region || SG.region];
      return r || (product.regions || {})[SG.region] || null;
    },

    /* Resolve the Shopify variant for the chosen options in this market. --- */
    lineFor: function (product, region, options) {
      region = region || SG.region;
      var r = SG.catalog.forRegion(product, region);
      var key = SG.catalog.optionKey(product, options, region);

      var variantId = null;
      if (r && r.variantMap && r.variantMap[key]) variantId = r.variantMap[key];
      else if (r && r.defaultVariantId) variantId = r.defaultVariantId;
      else {
        /* Fall back to the product's own variants when no regional map exists. */
        var wanted = SG.catalog.optionsFor(product, region).map(function (o) { return (options || {})[o.name] || o.values[0]; });
        var hit = (product.variants || []).filter(function (v) {
          return v.options.join('|') === wanted.join('|');
        })[0];
        variantId = hit ? hit.id : product.defaultVariantId;
      }

      var base = {
        handle: product.handle,
        title: product.title,
        market: region,
        shopifyProductId: r ? r.shopifyProductId : null,
        shopifyVariantId: variantId,
        printify: r ? r.printify : null,
        optionKey: key,
        options: Object.assign({}, options)
      };

      if (product.bundle) {
        base.components = (product.bundle.components || []).map(function (c) {
          var cr = (c.regions || {})[region] || {};
          /* Each half is resolved by only the options that belong to it, so the
             case and the wallet reach their own Shopify variants. */
          var ckey = SG.catalog.optionKey(product, options, region, c.options || null);
          return {
            key: c.key,
            title: c.title,
            sameAs: c.sameAs || null,
            surface: c.surface || null,
            optionKey: ckey,
            optionNames: c.options || [],
            shopifyProductId: cr.shopifyProductId || null,
            shopifyVariantId: (cr.variantMap && cr.variantMap[ckey]) || cr.defaultVariantId || null,
            printify: cr.printify || null
          };
        });
      }
      return base;
    },

    recommend: function (handle, count) {
      var p = byHandle[handle];
      var pool = PRODUCTS.filter(function (x) { return x.handle !== handle && x.personalisable; });
      var score = function (x) {
        var s = 0;
        if (p) {
          (x.collections || []).forEach(function (c) { if ((p.collections || []).indexOf(c) !== -1) s += 2; });
          if (x.vendor === p.vendor) s += 1;
        }
        if ((x.collections || []).indexOf('top') !== -1) s += 2;
        if (x.badge) s += 1;
        return s;
      };
      return pool.sort(function (a, b) { return score(b) - score(a); }).slice(0, count || 3);
    }
  };

  /* ============================================================ SHOPIFY URLS ==*/
  var root = (shop.routes && shop.routes.root) || '/';
  SG.url = function (p) {
    if (!p) return root;
    if (/^(https?:|mailto:|tel:|#|\/)/.test(p)) return p;
    var m = String(p).match(/^(index|product|collection|fitting-room|page|account)\.html(?:\?(.*))?$/);
    if (!m) return p;
    var q = new URLSearchParams(m[2] || '');
    if (m[1] === 'index') return root;
    if (m[1] === 'product') {
      var h = q.get('p');
      var prod = h && byHandle[h];
      return prod ? prod.url : root + 'collections/all';
    }
    if (m[1] === 'collection') return root + 'collections/' + (q.get('c') || 'all');
    if (m[1] === 'fitting-room') {
      return root + 'pages/fitting-room' + (q.get('p') ? '?p=' + q.get('p') : '');
    }
    /* Content pages are Shopify Pages. The slug is the page handle, with one
       exception: "checkout" is not a page — it is the real checkout. */
    if (m[1] === 'page') {
      var slug = q.get('p') || '';
      if (slug === 'checkout') return SG.checkoutUrl();
      return slug ? root + 'pages/' + slug : root;
    }
    if (m[1] === 'account') return SG.accountUrl();
    return p;
  };
  SG.go = function (p) { global.location.href = SG.url(p); };
  SG.query = function (k) { return new URLSearchParams(global.location.search).get(k); };

  /* Account and checkout resolve to Shopify's own routes here. Everything in
     the front end asks through these two functions, so nothing hard-codes a
     path — see core.js for the static-build defaults they replace. */
  SG.accountUrl  = function () { return (shop.routes && shop.routes.account)  || (root + 'account'); };
  SG.checkoutUrl = function () { return (shop.routes && shop.routes.checkout) || '/checkout'; };

}(window));
